import $ from 'jquery';
import { useOktaAuth } from '@okta/okta-react';
import { useHistory } from 'react-router-dom';

$.ajaxSetup({
  cache: false,
  xhrFields: {
    withCredentials: true
  }
});

interface IRestClient {
  baseURL: string;
  get: (url: string) => Promise<any>;
  getAuth: (url: string) => Promise<any>;
  post: (url: string, data: any) => Promise<any>;
  postAuth: (url: string, data: any) => Promise<any>;
  postFileAuth: (url: string, data: any, file: any) => Promise<any>;
  putAuth: (url: string, data: any) => Promise<any>;
  deleteAuth: (url: string) => Promise<any>;
  fetchWrapper: (url: string, requestInit?: RequestInit, requestType?: 'blob' | 'json' | 'text') => Promise<any>;
}

const useRestService = () => {
  const baseURL = window.env.BASE_API;
  const { oktaAuth } = useOktaAuth();
  const history = useHistory();

  const get = (url: string) => {
    const options = {
      headers: {
        'Content-Type': 'application/json'
      },
      crossDomain: true
    };

    return $.ajax(baseURL + url, options);
  };

  const getAuth = (url: string) => {
    let token = oktaAuth.getAccessToken();

    if (!token) {
      return $.Deferred().reject('Access token required');
    }

    const options = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token
      },
      xhrFields: {
        withCredentials: true
      },
      crossDomain: true
    };
    return $.ajax(baseURL + url, options).catch((error: any) => {
      return processError(error, false);
    });
  };

  const postAuth = (url: string, data: any) => {
    let token = oktaAuth.getAccessToken();

    if (!token) {
      return $.Deferred().reject('Access token required');
    }

    const options = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token
      },
      type: 'POST',
      data: JSON.stringify(data),
      xhrFields: {
        withCredentials: true
      },
      crossDomain: true
    };
    return $.ajax(baseURL + url, options).catch((error: any) => {
      return processError(error, true);
    });
  };

  const postFileAuth = (url: string, data: any, file: File) => {
    let token = oktaAuth.getAccessToken();

    if (!token) {
      return $.Deferred().reject('Access token required');
    }

    var formData = new FormData();
    formData.append('file', file);
    formData.append('data', data);

    const options = {
      headers: {
        Authorization: 'Bearer ' + token
      },
      type: 'POST',
      data: formData,
      processData: false,
      contentType: false,
      xhrFields: {
        withCredentials: true
      },
      crossDomain: true
    };
    return $.ajax(baseURL + url, options).catch((error: any) => {
      return processError(error, true);
    });
  };

  const post = (url: string, data: any) => {
    const options = {
      type: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      proccessData: false,
      data: JSON.stringify(data),
      xhrFields: {
        withCredentials: true
      },
      crossDomain: true
    };
    return $.ajax(baseURL + url, options).catch((error: any) => {
      return processError(error, true);
    });
  };

  const putAuth = (url: string, data: any) => {
    let token = oktaAuth.getAccessToken();

    if (!token) {
      return $.Deferred().reject('Access token required');
    }

    const options = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token
      },
      type: 'PUT',
      data: JSON.stringify(data),
      xhrFields: {
        withCredentials: true
      },
      crossDomain: true
    };
    return $.ajax(baseURL + url, options).catch((error: any) => {
      return processError(error, true);
    });
  };

  const deleteAuth = (url: string) => {
    let token = oktaAuth.getAccessToken();

    if (!token) {
      return $.Deferred().reject('Access token required');
    }

    const options = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token
      },
      type: 'DELETE',
      xhrFields: {
        withCredentials: true
      },
      crossDomain: true
    };
    return $.ajax(baseURL + url, options).catch((error: any) => {
      return processError(error, true);
    });
  };

  const processError = (error: any, returnError: boolean) => {
    if (error.status === 401 || error.status === 403) {
      localStorage.removeItem('firstName');
      localStorage.removeItem('lastName');

      oktaAuth.revokeAccessToken();
      oktaAuth.tokenManager.clear();
      oktaAuth.session.close();

      history.push('/login');
    }

    if (returnError) {
      return error;
    }
  };

  /**
   * Wrapper for fetch for use with React Query: throws Error on HTTP issues
   */
  const fetchWrapper = async (url: string, requestInit?: RequestInit, responseType?: 'json' | 'blob' | 'text') => {
    const response = await fetch(baseURL + url, {
      headers: {
        Authorization: `Bearer ${oktaAuth.getAccessToken()}`,
        'Content-Type': 'application/json'
      },
      // By default, prevent fetch from using stale browser cache data. Check with server to validate whether the
      // previous response is still valid. Override with requestInit if desired.
      cache: 'no-cache',
      ...requestInit
    });
    if (!response.ok) {
      const isJson = response.headers.get('content-type')?.includes('application/json');
      const data = isJson ? await response.json() : null;

      let message: string;

      if (data && 'message' in data) {
        message = data.message
      } else {
        message = response.statusText
      }

      throw new Error(`HTTP ${response.status} Error${message && ': ' + message}`);
    }
    if (responseType === 'text') {
      return response.text();
    }
    if (responseType === 'blob') {
      return response.blob();
    }
    return response.json();
  };

  return {
    get,
    getAuth,
    postAuth,
    postFileAuth,
    post,
    putAuth,
    deleteAuth,
    baseURL,
    fetchWrapper
  } as IRestClient;
};
export default useRestService;
